import React, { useEffect, useState } from "react";
import Input from "antd/es/input/Input";
import { Button, DatePicker, message, Pagination, Space, Table, Tooltip, Upload } from "antd";
import axios from "axios";
import { getDocFileBase64, getReport, deleteReport, uploadReport } from "../../apis/SummarySheetAPI";
import { useDispatch, useSelector } from "react-redux";
import Dragger from "antd/es/upload/Dragger";
import { DeleteColumnOutlined, DeleteFilled, DownloadOutlined, ExclamationCircleOutlined, InboxOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { formatDateToYYYYMMDD } from "../../libs/utilities/helpers/dateFormatter";
import { hideInfoDialog, showInfoDialog } from "../../reducers/infoDialogSlice";
import InfoDialog from "../../components/InfoDialog";
import { CloseRounded } from "@mui/icons-material";
import * as XLSX from 'xlsx';
import { encryptData } from "../../libs/utilities/helpers/Crypto.js"
import { useLocation } from "react-router-dom";
import {getTitleFromPath} from "../../libs/utilities/helpers/getTitle"
const { RangePicker } = DatePicker;

const SummarySheet = () => {
    const dispatch = useDispatch();
    const [srNumber, setSrNumber] = useState("");
    const [searchText, setSearchText] = useState('');
    const [allReport, setAllReport] = useState([]);
    const [fetchLoading, setFetchLoading] = useState(false);
    const [tableSorter, setTableSorter] = useState(null);
    const [selectedDocument, setSelectedDocument] = useState([]);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [filteredReport, setFilteredReport] = useState([]);
    const [filteredReportWithoutSort, setFilteredReportWithoutSort] = useState([]);
    // Regular expression to match HTML tags (e.g., <script>, <div>, etc.)
    const location = useLocation();
    const htmlTagRegex = /<\/?[\w\s]*>|<.*?>/g;
    const startDate = formatDateToYYYYMMDD(dayjs().subtract(6, 'days'));
        const endDate = formatDateToYYYYMMDD(dayjs());
        const [pagination, setPagination] = useState({
            current: 1,
            pageSize: 3,
            total: 0
        })

    useEffect(() => {
        setDateRange({ startDate, endDate })
        fetchReport(startDate, endDate);
    }, []);

    const startIdx = (pagination.current - 1) * pagination.pageSize;
    const endIdx = startIdx + pagination.pageSize;
    const displayData = filteredReport.slice(startIdx, endIdx)
    const dateFormat = 'YYYY-MM-DD';
     const defaultDateRange = [
            dayjs().subtract(6, 'days'),
            dayjs()
        ];
        const [dateRange, setDateRange] = useState({
            startDate: '',
            endDate: '',
        })

    const handleFolderName = (e) => {
        setSrNumber(e.target.value);
    };

    
const handleDownload = async () => {
    // Regex to detect HTML tags (for security)
    const htmlTagRegex = /<\/?[\w\s]*>|<.*?>/g;

    if (!srNumber || srNumber.trim() === "") {
        message.destroy();
        message.error("Please enter SR number like 1-123456789123 or policy number as 12345678");
        return;
    }

    if (htmlTagRegex.test(srNumber)) {
        message.destroy();
        message.error("Invalid input: HTML tags are not allowed.");
        return;
    }

    const input = srNumber.trim();

    // SR Number: digits and hyphen only, length 12-16
    const srNumberRegex = /^[0-9\-]{12,16}$/;
    // Policy Number: digits only, length 6-10
    const policyNumberRegex = /^[0-9]{6,10}$/;

    const isValidSR = srNumberRegex.test(input);
    const isValidPolicy = policyNumberRegex.test(input);

    if (!isValidSR && !isValidPolicy) {
        message.destroy();
        message.error("Invalid input. Please enter a valid SR number or Policy number.");
        return;
    }

    try {
        // Encrypt the input value before sending
        const encryptedPayload =  encryptData(input);
        if (!encryptedPayload || !encryptedPayload.data) {
            message.destroy();
            message.error("Something went wrong. Please try again.");
            return;
        }

        // Send the encrypted string as `id`
        const param = { id: encryptedPayload.data };

        const response = await dispatch(getDocFileBase64(param));
        
        if (response.error) {
            message.destroy();
            if (response?.payload?.statusCode === 'ERR0046') {
                message.error("Please enter a valid SR number or policy number");
            } else {
                message.error("Error in fetching the document");
            }
            return;
        }

        if (response?.payload?.statusCode === 200 && response?.payload?.data?.data == null) {
            message.destroy();
            message.error("No data exists for this SR number or policy number");
            return;
        }

        // Decode base64 and download file
        const binaryData = atob(response.payload.data.data);
        const arrayBuffer = new ArrayBuffer(binaryData.length);
        const uint8Array = new Uint8Array(arrayBuffer);

        for (let i = 0; i < binaryData.length; i++) {
            uint8Array[i] = binaryData.charCodeAt(i);
        }

        const blob = new Blob([uint8Array], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);

        // Use input as filename, fallback to default if needed
        link.download = input || "download.docx";
        link.click();

        setSrNumber("");
    } catch (error) {
        message.destroy();
        message.error("Failed to download the file.");
    }
};

    const sortTableData = (pagination, filters, sorter) => {
        setTableSorter(sorter);
        if (sorter.columnKey === "document_name") {
            if (!sorter.order) {
                setFilteredReport(filteredReportWithoutSort);
                return;
            }
            const filtered = filteredReport.sort((a, b) =>
                sorter.order === "ascend" ? a.document_name.localeCompare(b.document_name) : b.document_name.localeCompare(a.document_name));
            setFilteredReport([...filtered]);
        }
    }

    const fetchReport = async (startDate, endDate) => { 

            const response = await dispatch(getReport({ startDate, endDate }));
            const data = await response;
            if (!data.error) {
                setSearchText('');
                // {document_name, is_processed, createdAt, updatedAt}
                const sortedData = data.payload.data.documents.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
                const lists = sortedData.map((item, index) => (
                    {
                        id: index,
                        document_id: item._id,
                        document_name: item.document_name,
                        is_processed: item.is_processed,
                        createdAt: formatDateToYYYYMMDD(item.createdAt), updatedAt: item.updatedAt
                    }));

                    
                setPagination({ ...pagination, total: lists.length })
                setAllReport(lists);
                setFilteredReport(lists);
                setFilteredReportWithoutSort([...lists]);
            }
            setFetchLoading(false); 
    }

    const confirmDelete = async () => { 
        try {
            const response = await dispatch(deleteReport({ _id: selectedId }));
            const data = await response;
            if (!data.error) {
                message.destroy();
                message.success("File deleted successfully");
            dispatch(hideInfoDialog());
            fetchReport(dateRange.startDate, dateRange.endDate)
            }
            setFetchLoading(false); 
        } catch {
            message.destroy();
            message.warning("Something went wrong!");
        }
    }

    
    const [selectedFile, setSelectedFile] = useState([]);

    const props = {
        name: 'file',
        multiple: false,
        showUploadList: false,
        maxCount: 1,
        beforeUpload: (file) => {
            const allowedMimeType = [
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/vnd.ms-excel'
            ];
        
            if (!allowedMimeType.includes(file.type)) {
                message.destroy();
                message.error("Invalid File format. File should be in .xlsx format");
                setSelectedFile([]);
                return Upload.LIST_IGNORE;
            }
        
            if ((file.size / 1024 / 1024) > 20) {
                message.destroy();
                message.error('File must be smaller than 20 MB');
                setSelectedFile([]);
                return Upload.LIST_IGNORE;
            }
        
            const reader = new FileReader();
            reader.onload = (e) => {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
                if (jsonData.length === 0) {
                    message.destroy();
                    message.error("Excel file is empty");
                    setSelectedFile([]);
                    return;
                }
        
                const headersRaw = jsonData[0];
                const headers = headersRaw.map(h => String(h).trim().toLowerCase());
        
                const requiredHeaders = ['Policy no', 'Activity', 'Sub activity', 'SR no', 'Case Received On', 'Complaints_ER']
                    .map(h => h.trim().toLowerCase());
        
                const missingHeaders = requiredHeaders.filter(header => !headers.includes(header));
        
                if (missingHeaders.length > 0) {
                    message.destroy();
                    message.error(`Missing required columns: ${missingHeaders.join(', ')}`);
                    setSelectedFile([]);
                    return;
                }
        
                // All validations passed
                setSelectedFile([file]);
            };
        
            reader.readAsArrayBuffer(file);
            return false; // Prevent auto-upload
        },
        
        
        onChange(file) {
            const data = file.fileList.map(item => item.originFileObj ? item.originFileObj : item)
          setSelectedFile(data);
        },
        disabled: selectedFile.length > 0
      };

      const [selectedId, setSelectedId] = useState('');

      const startDelete = (doc_id) => {
              setSelectedId(doc_id);
              dispatch(showInfoDialog({ title: "Are you sure you want to delete?", description: "" }));
          }

const hasInvalidChars = (value) => {
    return /[<>/"'`;()&*#$%!^?]/.test(value);
};
      const handleSearch = (e) => {
        const searchString = e.target.value;
        if (hasInvalidChars(searchString)) {
            message.destroy();
            message.error('Special characters are not allowed');
        return;
        }
        setSearchText(searchString);
        const allData = [...allReport];
        const filtered = allData.filter(item => item.document_name.toLowerCase().includes(searchString.trim().toLowerCase()))
        setPagination({ ...pagination, current: 1, pageSize: 3, total: filtered.length })

        setFilteredReport(filtered);
        setFilteredReportWithoutSort([...filtered]);

        if (tableSorter?.columnKey === "document_name") {
            if (!tableSorter.order) {
                setFilteredReport(filteredReportWithoutSort);
                return;
            }
            const tempFiltered = filtered.sort((a, b) =>
                tableSorter.order === "ascend" ? a.document_name.localeCompare(b.document_name) : b.document_name.localeCompare(a.document_name));
            setFilteredReport([...tempFiltered]);
        }

    }

    const columns = [
        {
            title: 'Name',
            dataIndex: 'document_name',
            key: 'document_name',
            sorter: (a, b) => { }
        },
        {
            title: 'Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            // sorter: (a,b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        },
        {
            title: 'Status',
            dataIndex: 'is_processed',
            key: 'is_processed',
            render: (_, record) => (
                <> {record.is_processed ? <div style={{ color: "" }}>Completed</div> : <div style={{ color: "" }}>In Progress</div>} </>
            )
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Space size="small">
                    {
                        !record.is_processed ?
                            <Tooltip
                                placement="top" title="Delete document"
                            >
                                <DeleteFilled onClick={() => startDelete(record.document_id)} />
                            </Tooltip>
                            :
                            <Tooltip title="Document not generated">
                                <Button type="text"><ExclamationCircleOutlined style={{ color: "gray" }} /></Button>
                            </Tooltip>
                    }
                    
                </Space>
            ),
        },
    ];

    
    
     const handleDateFilter = (dates, dateStrings) => {
         if (!dates) {
             return;
            }
            let startDate = formatDateToYYYYMMDD(dateStrings[0]);
            let endDate = formatDateToYYYYMMDD(dateStrings[1]);
            setDateRange({ startDate, endDate })
            fetchReport(startDate, endDate);
        };

        const getEndOfDay = (date) => {
            const endOfDay = new Date(date);
            endOfDay.setHours(23, 59, 59, 999);
            return endOfDay;
        };

        const disabledDate = current => {
            return current && current > getEndOfDay(new Date());
        };

        const handlePageChange = (page, pageSize) => {
            setPagination({ ...pagination, current: page, pageSize })
        }
        const onRemove = () => {
            setSelectedFile([]);
        }


        const uploadFile = async () => {
            try {
                setUploadLoading(true)
                const data = new FormData();
                data.append("file", selectedFile[0])
                const res = await dispatch(uploadReport(data));
                setSelectedFile([])
                setUploadLoading(false);
                if(!res.error) {
                    fetchReport(dateRange.startDate, dateRange.endDate);
                    message.success("File uploaded successfully");
                }
            } catch  (e)  {
                setUploadLoading(false);
            }
        }
    return (
        <>
 <div className="heading-div">
        <p className="text5">
          {getTitleFromPath(location ?.pathname)}
        </p>
      </div>
        <InfoDialog onConfirm={confirmDelete} />
            <div style={{ padding: "20px", marginTop: "20px", gap: "40px", display: "flex", alignItems:"center", background: "white", marginBottom: "20px", borderRadius: "7px", }}>
                <div style={{ borderBottom: "2px solid #00538A", width: "100%"}}>
                <input
                    value={srNumber}
                    onChange={handleFolderName}
                    style={{  height: "40px",   border: "0", width: "100%" }}
                    placeholder="Please enter SR number like 1-123456789123 or policy number as 12345678"
                />

                </div>
                <Button type="primary" onClick={handleDownload}
                     style={{ height: "40px"}}>
                    <div className=''>Search</div>
                </Button>
            </div>

            <div style={{ display: "flex", gap: "20px", height: "100%" }}>
                <div style={{ flex: 0.7, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "7px" }}  className="file-table">
                    <div className="table-actions" style={{ display: "flex", width: "100%", justifyContent: "space-between"}}>
                        <div style={{ width: "200px" }}>
                            <Input size="middle" style={{ borderRadius: "5px" }} value={searchText} onChange={(e) => handleSearch(e)} placeholder="Search" />
                        </div>
                        <div style={{ marginLeft: '0'}}>
                            <RangePicker
                                className="file-datepicker"
                                onChange={handleDateFilter}
                                disabledDate={disabledDate}
                                defaultValue={defaultDateRange}
                                format={dateFormat}
                                allowClear={false}
                            />
                        </div>
                    </div>
                    <Table 
                        columns={columns}
                        dataSource={displayData}
                        loading={fetchLoading}
                        style={{ overflowY: "auto", height: "100%", background: "white" }}
                        pagination={false}
                        rowKey={(record) => record.id}
                        onChange={sortTableData}
                        sticky={{ width: "100%"}}
                        
                    />
                    <Pagination
                        current={pagination.current}
                        pageSize={pagination.pageSize}
                        total={+pagination.total}
                        onChange={handlePageChange} style={{ marginTop: "5px", float: "right" }} />
                </div>

                <div className="summary-sheet" style={{background: "white", padding: "23px 10px 9px 10px", borderRadius: '5px', flex: 0.3, height: "100%", minWidth: "200px", display: "flex", flexDirection: "column", gap: "5px", alignItems: "flex-end" }}>
                    <Dragger {...props} className="dragger-upload">
                        <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                        </p>
                        {/* <p className="ant-upload-text">Click or drag file</p> */}
                        <div style={{ overflow: "scroll" }}>
                        {
                            selectedFile.length === 0 &&
                                <div className='upload-action'>
                                    <div className='title'>Drag and drop here or</div>
                                    <button className="btn-outlined">
                                        <div className='btn-outlined-content'>
                                            <div className='text'>Upload documents </div>
                                            {/* <img width={29} src={ArrowUpIcon} /> */}
                                        </div>
                                    </button>
                                </div>
                            }
                            {
                                selectedFile.map(item => (
                                    <div className="upload-action" style={{ zIndex: 9999 }} onClick={() => { return; }}>
                                        <div className="selectedList">
                                            <div className="name">{item.name}</div>
                                            <CloseRounded className="remove" onClick={() => onRemove()} />
                                        </div>
                                    </div>
                                ))
                            }
                        </div>
                    </Dragger>
                    <div style={{}}>
                    <Button 
  type="primary" 
  onClick={() => uploadFile()} 
  disabled={uploadLoading || selectedFile.length === 0}
>
  {uploadLoading ? 'Loading' : 'Upload'}
</Button>

                    </div>
                </div>
            </div>
        </>
    );
};

export default SummarySheet;
